import {
  Alert,
  Box,
  Button,
  ColorInput,
  FileButton,
  Grid,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BRANDING_LOGO_MAX_BYTES,
  BRANDING_LOGO_MIMES,
  hexColorSchema,
  websiteUrlSchema,
  type Branding,
  type BrandingSuggestResponse,
  type StageBranding,
  type StageSnapshot,
} from "@msc/shared";

import {
  brandingLogoUrl,
  useBranding,
  useRemoveLogo,
  useResetBranding,
  useSuggestBranding,
  useUpdateBranding,
  useUploadLogo,
} from "../../hooks/useBranding.js";
import { ApiError } from "../../lib/api.js";
import { MINDSQUARE_SWATCHES, resolveStageTheme } from "../../lib/customerView/brandingTheme.js";
import { GERMAN_DATE_FORMAT } from "../../lib/date.js";
import { describeApiError } from "../../lib/errorText.js";
import { StageSlides } from "../stage/StageSlides.js";

import "../stage/stage.css";

/** Fehlercodes des Vorschlags, zu denen die Oberfläche einen eigenen Satz hat. */
const SUGGEST_ERROR_KEYS: Readonly<Record<string, string>> = {
  BRANDING_NO_WEBSITE: "pages.customers.branding.suggestNoWebsite",
  BRANDING_WEBSITE_UNREACHABLE: "pages.customers.branding.suggestUnreachable",
  BRANDING_SUGGEST_RATE_LIMITED: "pages.customers.branding.suggestRateLimited",
};

/** Dieselbe Regel wie der Server — die Oberfläche fragt vorher, damit niemand 512 KB umsonst hochlädt. */
export function logoProblem(file: { type: string; size: number }): "logoType" | "logoTooLarge" | null {
  if (!(BRANDING_LOGO_MIMES as readonly string[]).includes(file.type)) return "logoType";
  if (file.size > BRANDING_LOGO_MAX_BYTES) return "logoTooLarge";
  return null;
}

/** `#E30613`, `e30613` → `#e30613`; leer → null; alles andere → undefined (ungültig). */
function normalizeColor(value: string): string | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const withHash = (trimmed.startsWith("#") ? trimmed : `#${trimmed}`).toLowerCase();
  return hexColorSchema.safeParse(withHash).success ? withHash : undefined;
}

/**
 * Reiter „Präsentation" der Kundenakte (D10): Logo und zwei Farben, in denen
 * die Bühne der Customer View für diesen Kunden läuft.
 *
 * Der KI-Vorschlag ist ein Angebot des Servers: Er holt Logo und Farben von
 * der Website und speichert sie sofort — der Lead sieht das Ergebnis hier und
 * überschreibt, was nicht passt. Jede Änderung von Hand macht aus der
 * Herkunft „manuell".
 *
 * Schreiben dürfen dieselben wie bei den Beauftragungen UND der Service Lead
 * des Kunden (er richtet den Termin ein); die Grenze zieht der Server, die
 * Oberfläche sperrt die Felder nur zusätzlich.
 */
export function BrandingPanel({
  customerId,
  customerName,
  canEdit,
}: {
  customerId: string;
  customerName: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const branding = useBranding(customerId);

  if (branding.isLoading) {
    return (
      <Stack gap="sm">
        <Skeleton height={36} />
        <Skeleton height={120} />
      </Stack>
    );
  }
  if (branding.isError || !branding.data) {
    return (
      <Alert color="red" title={t("pages.customers.branding.loadError")}>
        {describeApiError(branding.error, t)}
      </Alert>
    );
  }
  return (
    <BrandingForm
      branding={branding.data}
      customerName={customerName}
      canEdit={canEdit}
    />
  );
}

function BrandingForm({
  branding,
  customerName,
  canEdit,
}: {
  branding: Branding;
  customerName: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const customerId = branding.customerId;
  const update = useUpdateBranding(customerId);
  const upload = useUploadLogo(customerId);
  const removeLogo = useRemoveLogo(customerId);
  const reset = useResetBranding(customerId);
  const suggest = useSuggestBranding(customerId);

  const [website, setWebsite] = useState(branding.websiteUrl ?? "");
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [primary, setPrimary] = useState(branding.primaryColor ?? "");
  const [accent, setAccent] = useState(branding.accentColor ?? "");
  const [colorError, setColorError] = useState<{ primary?: string; accent?: string }>({});
  const [logoError, setLogoError] = useState<string | null>(null);
  const [suggestResult, setSuggestResult] = useState<BrandingSuggestResponse | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  // Ein neuer gespeicherter Stand (KI-Vorschlag, Zurücksetzen, Antwort auf
  // eine Änderung) zieht die Felder nach. Bewusst je Feld statt eines
  // Neuaufbaus der ganzen Maske: Das Ergebnis des Vorschlags soll stehen
  // bleiben, während die Farben darunter umspringen.
  // Farbwähler melden das Ende einer Auswahl UND danach den Fokusverlust —
  // derselbe Wert soll nur einmal an den Server gehen. Steht ein neuer Stand
  // fest, zählt wieder nur er.
  const lastSent = useRef<{ primary?: string | null; accent?: string | null }>({});
  useEffect(() => {
    setPrimary(branding.primaryColor ?? "");
    lastSent.current.primary = undefined;
  }, [branding.primaryColor]);
  useEffect(() => {
    setAccent(branding.accentColor ?? "");
    lastSent.current.accent = undefined;
  }, [branding.accentColor]);
  useEffect(() => setWebsite(branding.websiteUrl ?? ""), [branding.websiteUrl]);

  const onSaved = () => notifications.show({ color: "green", message: t("pages.customers.branding.saved") });
  const onFailed = (err: unknown) => notifications.show({ color: "red", message: describeApiError(err, t) });

  function saveWebsite() {
    const trimmed = website.trim();
    const next = trimmed === "" ? null : trimmed;
    if (next === (branding.websiteUrl ?? null)) {
      setWebsiteError(null);
      return;
    }
    if (next !== null && !websiteUrlSchema.safeParse(next).success) {
      setWebsiteError(t("pages.customers.branding.websiteInvalid"));
      return;
    }
    setWebsiteError(null);
    update.mutate({ websiteUrl: next }, { onSuccess: onSaved, onError: onFailed });
  }

  function saveColor(which: "primary" | "accent", value: string) {
    const normalized = normalizeColor(value);
    if (normalized === undefined) {
      setColorError((e) => ({ ...e, [which]: t("pages.customers.branding.colorInvalid") }));
      return;
    }
    setColorError((e) => ({ ...e, [which]: undefined }));
    const saved = which === "primary" ? branding.primaryColor : branding.accentColor;
    if (normalized === saved || lastSent.current[which] === normalized) return;
    lastSent.current[which] = normalized;
    update.mutate(which === "primary" ? { primaryColor: normalized } : { accentColor: normalized }, {
      onSuccess: onSaved,
      onError: (err) => {
        lastSent.current[which] = undefined;
        onFailed(err);
      },
    });
  }

  function pickLogo(file: File | null) {
    if (!file) return;
    const problem = logoProblem(file);
    if (problem !== null) {
      setLogoError(t(`pages.customers.branding.${problem}`));
      return;
    }
    setLogoError(null);
    upload.mutate(file, { onSuccess: onSaved, onError: (err) => setLogoError(describeApiError(err, t)) });
  }

  function runSuggest() {
    setSuggestError(null);
    setSuggestResult(null);
    suggest.mutate(undefined, {
      onSuccess: (response) => setSuggestResult(response),
      onError: (err) => {
        const key = err instanceof ApiError ? SUGGEST_ERROR_KEYS[err.code] : undefined;
        setSuggestError(key ? t(key) : describeApiError(err, t));
      },
    });
  }

  const logoUrl = brandingLogoUrl(branding);

  // Die Vorschau folgt den Entwürfen, sobald sie gültig sind — wer im
  // Farbwähler zieht, sieht die Wirkung, bevor gespeichert ist.
  const previewBranding = useMemo<StageBranding>(() => {
    const p = normalizeColor(primary);
    const a = normalizeColor(accent);
    return {
      logoUrl,
      primaryColor: p === undefined ? branding.primaryColor : p,
      accentColor: a === undefined ? branding.accentColor : a,
    };
  }, [primary, accent, logoUrl, branding.primaryColor, branding.accentColor]);

  const previewSnapshot = useMemo<StageSnapshot>(
    () => ({
      mode: "pause",
      theme: "customer",
      customerName,
      deck: [],
      currentIndex: -1,
      current: null,
      stats: null,
      events: null,
      agendaItems: [],
      agendaIndex: -1,
      branding: previewBranding,
    }),
    [customerName, previewBranding],
  );
  const preview = resolveStageTheme(previewSnapshot);

  const sourceLine =
    branding.source === "ki" && branding.suggestedAt
      ? t("pages.customers.branding.sourceKi", { date: dayjs(branding.suggestedAt).format(GERMAN_DATE_FORMAT) })
      : branding.source === "manuell"
        ? t("pages.customers.branding.sourceManual")
        : t("pages.customers.branding.sourceNone");

  const hasRecord = branding.updatedAt !== null;
  const busy = update.isPending || upload.isPending || removeLogo.isPending || reset.isPending || suggest.isPending;

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {t("pages.customers.branding.intro")}
      </Text>
      {!canEdit && (
        <Alert color="gray" variant="light">
          {t("pages.customers.branding.readOnly")}
        </Alert>
      )}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap="md">
            <Stack gap={6}>
              <TextInput
                label={t("pages.customers.branding.website")}
                description={t("pages.customers.branding.websiteHint")}
                placeholder={t("pages.customers.branding.websitePlaceholder")}
                value={website}
                onChange={(e) => setWebsite(e.currentTarget.value)}
                onBlur={saveWebsite}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveWebsite();
                }}
                error={websiteError}
                disabled={!canEdit}
                maxLength={500}
              />
              <Group gap="sm" align="center">
                <Button
                  variant="light"
                  onClick={runSuggest}
                  loading={suggest.isPending}
                  disabled={!canEdit || !branding.websiteUrl || busy}
                >
                  {t("pages.customers.branding.suggest")}
                </Button>
                {suggest.isPending && (
                  <Text size="xs" c="dimmed">
                    {t("pages.customers.branding.suggesting")}
                  </Text>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {branding.websiteUrl ? t("pages.customers.branding.suggestHint") : t("pages.customers.branding.suggestNoWebsite")}
              </Text>
              {suggestResult && <SuggestResult result={suggestResult} />}
              {suggestError && (
                <Alert color="yellow" variant="light">
                  {suggestError}
                </Alert>
              )}
            </Stack>

            <Stack gap={6}>
              <Text size="sm" fw={500}>
                {t("pages.customers.branding.logo")}
              </Text>
              <Group gap="md" align="center" wrap="nowrap">
                <Box
                  w={200}
                  h={72}
                  p={8}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-sm)",
                    // Weiß wie die Plakette auf der Bühne — so sieht der Lead das Logo, wie der Kunde es sieht.
                    background: "#ffffff",
                    flexShrink: 0,
                  }}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={t("pages.customers.branding.logoAlt", { customer: customerName })}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <Text size="xs" c="dimmed" ta="center">
                      {t("pages.customers.branding.noLogo")}
                    </Text>
                  )}
                </Box>
                <Stack gap={6}>
                  <FileButton onChange={pickLogo} accept={BRANDING_LOGO_MIMES.join(",")} disabled={!canEdit || busy}>
                    {(props) => (
                      <Button {...props} size="xs" variant="default" loading={upload.isPending} disabled={!canEdit || busy}>
                        {logoUrl ? t("pages.customers.branding.replaceLogo") : t("pages.customers.branding.uploadLogo")}
                      </Button>
                    )}
                  </FileButton>
                  {logoUrl && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      loading={removeLogo.isPending}
                      disabled={!canEdit || busy}
                      onClick={() => removeLogo.mutate(undefined, { onSuccess: onSaved, onError: onFailed })}
                    >
                      {t("pages.customers.branding.removeLogo")}
                    </Button>
                  )}
                </Stack>
              </Group>
              <Text size="xs" c="dimmed">
                {t("pages.customers.branding.logoHint")}
              </Text>
              {logoError && (
                <Text size="xs" c="red">
                  {logoError}
                </Text>
              )}
            </Stack>

            <Group grow align="flex-start">
              <ColorInput
                label={t("pages.customers.branding.primaryColor")}
                description={t("pages.customers.branding.primaryColorHint")}
                format="hex"
                swatches={[...MINDSQUARE_SWATCHES]}
                swatchesPerRow={4}
                value={primary}
                onChange={setPrimary}
                onChangeEnd={(v) => saveColor("primary", v)}
                onBlur={() => saveColor("primary", primary)}
                error={colorError.primary}
                disabled={!canEdit}
                placeholder="#rrggbb"
              />
              <ColorInput
                label={t("pages.customers.branding.accentColor")}
                description={t("pages.customers.branding.accentColorHint")}
                format="hex"
                swatches={[...MINDSQUARE_SWATCHES]}
                swatchesPerRow={4}
                value={accent}
                onChange={setAccent}
                onChangeEnd={(v) => saveColor("accent", v)}
                onBlur={() => saveColor("accent", accent)}
                error={colorError.accent}
                disabled={!canEdit}
                placeholder="#rrggbb"
              />
            </Group>

            <Group justify="space-between" align="center">
              <Text size="xs" c="dimmed" data-testid="branding-source">
                {sourceLine}
              </Text>
              {hasRecord && (
                <Button size="xs" variant="subtle" color="red" disabled={!canEdit || busy} onClick={() => setResetOpen(true)}>
                  {t("pages.customers.branding.reset")}
                </Button>
              )}
            </Group>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap={6}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              {t("pages.customers.branding.preview")}
            </Text>
            {/* Dieselbe Verkleinerung wie die Vorschau im Cockpit: 16:9-Kasten, Folie in doppelter Breite halbiert. */}
            <Paper withBorder style={{ overflow: "hidden" }}>
              <Box style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", overflow: "hidden" }}>
                <Box
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "200%",
                    transform: "scale(.5)",
                    transformOrigin: "top left",
                  }}
                >
                  <div
                    className="stage-preview"
                    data-testid="branding-preview"
                    data-stage-theme={preview.theme}
                    style={preview.style}
                  >
                    <StageSlides snapshot={previewSnapshot} />
                  </div>
                </Box>
              </Box>
            </Paper>
            <Text size="xs" c="dimmed">
              {t("pages.customers.branding.previewHint")}
            </Text>
          </Stack>
        </Grid.Col>
      </Grid>

      <Modal opened={resetOpen} onClose={() => setResetOpen(false)} title={t("pages.customers.branding.resetTitle")} centered>
        <Stack gap="md">
          <Text size="sm">{t("pages.customers.branding.resetConfirm")}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setResetOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={reset.isPending}
              onClick={() =>
                reset.mutate(undefined, {
                  onSuccess: () => {
                    setResetOpen(false);
                    setSuggestResult(null);
                    onSaved();
                  },
                  onError: onFailed,
                })
              }
            >
              {t("pages.customers.branding.reset")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/** Was der Vorschlag gefunden hat — und was der Lead noch selbst setzen muss. */
function SuggestResult({ result }: { result: BrandingSuggestResponse }) {
  const { t } = useTranslation();
  const labels = {
    logo: t("pages.customers.branding.logo"),
    primaryColor: t("pages.customers.branding.primaryColor"),
    accentColor: t("pages.customers.branding.accentColor"),
  };
  const keys = ["logo", "primaryColor", "accentColor"] as const;
  const found = keys.filter((k) => result.found[k]).map((k) => labels[k]);
  const missing = keys.filter((k) => !result.found[k]).map((k) => labels[k]);
  return (
    <Alert color={missing.length === 0 ? "green" : "yellow"} variant="light" data-testid="branding-suggest-result">
      <Stack gap={2}>
        <Text size="sm">
          {t("pages.customers.branding.suggestDone", {
            how: result.viaLlm ? t("pages.customers.branding.suggestViaLlm") : t("pages.customers.branding.suggestFallback"),
          })}
        </Text>
        {found.length > 0 && <Text size="xs">{t("pages.customers.branding.suggestFound", { list: found.join(", ") })}</Text>}
        {missing.length > 0 && (
          <Text size="xs">{t("pages.customers.branding.suggestMissing", { list: missing.join(", ") })}</Text>
        )}
      </Stack>
    </Alert>
  );
}
